import { RubyLoader } from '../components/RubyLoader';
import { RubyGemMark } from '../components/RubyGemMark';
import React, { useState, useEffect, useCallback } from 'react';
import { formFactorLabel } from '../services/hardwareApi';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Monitor, Activity, Info, CreditCard, Settings, LayoutGrid, Cpu,
  Power, Archive, RotateCcw, Trash2, AlertTriangle, X,
} from 'lucide-react';
import { api, authedRequest } from '../services/apiService';
import { Skeleton, SkeletonRows } from '../components/Skeleton';
import PosTerminalDetailsTab from '../components/pos/PosTerminalDetailsTab';
import TerminalActivityCard, { type TerminalActivityData } from '../components/pos/TerminalActivityCard';
import PosPaymentTypesTab from '../components/pos/PosPaymentTypesTab';
import { useUnsavedChangesGuard } from '../components/pos/payment-types/UnsavedChangesGuard';
import { terminalDisplayStatus, DARK_BADGE, type TerminalDisplayStatus } from '../components/pos/terminalDisplayStatus';
import PosControlTab from '../components/pos/PosControlTab';
import PosButtonLayoutTab from '../components/pos/PosButtonLayoutTab';
import PosEmvDeviceTab from '../components/pos/PosEmvDeviceTab';
import PosOnlineTerminalsTab from '../components/pos/PosOnlineTerminalsTab';
import { useCatalogModel } from '../components/pos/cardSetup/useCatalogModel';
import { useDeviceModelReport, reportedHardware } from '../components/pos/cardSetup/useReportedModel';
import { ModelMismatchBanner } from '../components/pos/cardSetup/ModelMismatchBanner';
import type { TipLocation } from '../components/pos/cardSetup/cardSetupModel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Terminal {
  terminalId: string;
  provisionCode: string;
  locationId: string;
  locationName: string;
  registerName: string;
  terminalType: 'pos' | 'kiosk' | 'customer_display' | 'android_dual_screen' | 'online_sales';
  status: 'pending' | 'activated';
  createdAt: number;
  expiresAt?: number;
  activatedAt?: number;
  lastSeenAt?: number;
  /** The REGISTER row's status; null when the terminal was never claimed. */
  registerStatus?: string | null;
  /** online_sales only — the Dejavoo processor this terminal references. */
  emvDeviceId?: string | null;
  /** The catalog model the register was provisioned as (pos-terminals-list). */
  deviceModelId?: string | null;
}

type TabId = 'details' | 'payment-types' | 'emv-device' | 'control' | 'button-layout' | 'online-terminals';

/** SUNMI card setup: the EMV Device tab's name on a register whose model has a
 *  `cardSetupPolicy` (sunmi-card-setup.md §5). Same tab id, so links still work. */
const CARD_SETUP_TAB_LABEL = 'Terminal (EMV/SoftPOS)';

const TABS: Array<{ id: TabId; label: string; icon: React.FC<any> }> = [
  { id: 'details', label: 'Details', icon: Info },
  { id: 'payment-types', label: 'Payment Types', icon: CreditCard },
  { id: 'emv-device', label: 'EMV Device', icon: Cpu },
  { id: 'control', label: 'Control', icon: Settings },
  { id: 'button-layout', label: 'Button Layout', icon: LayoutGrid },
];

// Online-sales terminals have no physical hardware / layout — only their basic
// details and the online payment processor apply.
const ONLINE_TABS: Array<{ id: TabId; label: string; icon: React.FC<any> }> = [
  { id: 'details', label: 'Details', icon: Info },
  { id: 'online-terminals', label: 'Online Payment', icon: CreditCard },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * ONE source for terminal-type wording — `FORM_FACTOR_LABEL` in shared-types.
 *
 * This was a local `switch`, and there were five of them. They had already
 * drifted: the two in platform-app did not handle `android_dual_screen` at all,
 * so a K10 showed a tenant the raw string `android_dual_screen`.
 */
const typeLabel = (t: string) => formFactorLabel(t);

const statusBadge = (d: TerminalDisplayStatus) => (
  <span title={d.hint} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${DARK_BADGE[d.tone]}`}>
    {d.label}
  </span>
);

// WebSocket status API
const getWsStatusUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const base = hostname.includes('dev.') || hostname === 'localhost' ? 'dev.rubytill.com' : 'rubytill.com';
  return `https://pos-ws.api.${base}`;
};

// Terminal Activity (pos-register) — same read as the merchant terminal page.
const getPosRegisterUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const base = hostname.includes('dev.') || hostname === 'localhost' ? 'dev.rubytill.com' : 'rubytill.com';
  return `https://pos-register.api.${base}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SuperAdminPosTerminalDetail: React.FC = () => {
  const { tenantId, terminalId } = useParams<{ tenantId: string; terminalId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Terminal can be passed via navigation state (from list click) or fetched
  const navState = location.state as { terminal?: Terminal; tenantName?: string } | null;

  const [terminal, setTerminal] = useState<Terminal | null>(navState?.terminal || null);
  const [tenantName] = useState(navState?.tenantName || '');
  const [loading, setLoading] = useState(!navState?.terminal);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(false);
  // Online-terminal lifecycle: active → inactive (deactivate) → archived (soft,
  // recoverable). Permanent delete only from archived. Nothing destructive is a
  // single click; archive is fully reversible.
  const [lifeStatus, setLifeStatus] = useState<string>((navState?.terminal?.status) || 'active');
  const [lifeBusy, setLifeBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'archive' | 'delete' | null>(null);
  const [lifeMsg, setLifeMsg] = useState<string | null>(null);

  const runLifecycle = async (fn: () => Promise<void>) => {
    setLifeBusy(true); setLifeMsg(null);
    try { await fn(); } catch (e: any) { setLifeMsg(e?.message || 'Action failed'); }
    finally { setLifeBusy(false); }
  };
  const setStatus = (registerId: string, status: 'active' | 'inactive' | 'archived') => runLifecycle(async () => {
    await api.posNmi.updateOnlineTerminal(tenantId!, registerId, { status });
    setLifeStatus(status);
    setConfirmAction(null);
  });
  const deletePermanently = (registerId: string) => runLifecycle(async () => {
    await api.posNmi.deleteOnlineTerminal(tenantId!, registerId);
    navigate(`/tenant/${tenantId}?tab=pos`);
  });
  useEffect(() => { if (terminal?.status) setLifeStatus(terminal.status); }, [terminal?.status]);

  /* SUNMI card setup — the register's catalog model and what the till reports.
     `catalog.policy` is non-null ONLY for a model with a cardSetupPolicy; every
     other register keeps the EMV Device tab exactly as it was. */
  const [modelKey, setModelKey] = useState(0);
  const catalog = useCatalogModel(terminal?.terminalType === 'online_sales' ? null : terminal?.deviceModelId, modelKey);
  const modelReport = useDeviceModelReport(tenantId, terminal && terminal.terminalType !== 'online_sales' ? terminal.terminalId : null, modelKey);
  const reported = reportedHardware(modelReport);
  const reportedBuildTarget = (modelReport?.reported?.buildTarget || '').trim() || null;
  /* SUNMI before any card setup: a tip location picked on Payment Types is
     carried to the Terminal tab and saved with the readers. */
  const [pendingTip, setPendingTip] = useState<TipLocation | null>(null);
  const onModelChanged = useCallback((modelId: string) => {
    setTerminal((t) => (t ? { ...t, deviceModelId: modelId } : t));
    setModelKey((k) => k + 1);
  }, []);

  // Tab state is driven by the URL (?tab=emv-device) so tabs are deep-linkable
  // and back-button-safe. TABS is the source of truth for valid ids.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') as TabId | null;
  // Validate against ALL known tab ids (physical + online), else a valid tab like
  // ?tab=online-terminals falls back to Details and looks "not selectable".
  const activeTab: TabId = (urlTab && [...TABS, ...ONLINE_TABS].some(t => t.id === urlTab)) ? urlTab : 'details';
  /* The Payment Types tab holds unsaved edits in one save bar; leaving the
     tab or the page with any asks first, in our own dialog. */
  const [paymentTypesDirty, setPaymentTypesDirty] = useState(false);
  const leaveGuard = useUnsavedChangesGuard(paymentTypesDirty);
  const setActiveTab = (id: TabId) => leaveGuard.requestLeave(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: false });
  });

  // Fetch terminal from list API if not passed via state (e.g. direct URL navigation)
  const fetchTerminal = useCallback(async () => {
    if (!tenantId || !terminalId) return;
    try {
      setLoading(true);
      setError(null);
      const resp = await api.posTerminals.list(tenantId);
      const list: Terminal[] = resp?.terminals || [];
      const found = list.find((t) => t.terminalId === terminalId);
      if (!found) {
        setError('Terminal not found');
      } else {
        setTerminal(found);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load terminal');
    } finally {
      setLoading(false);
    }
  }, [tenantId, terminalId]);

  // Fetch WebSocket online status
  const fetchWsStatus = useCallback(async () => {
    if (!tenantId || !terminalId) return;
    try {
      const data = await authedRequest<{ terminals?: Array<{ terminalId: string }> }>(
        `${getWsStatusUrl()}/terminals/status?tenantId=${tenantId}`,
        { headers: { 'x-tenant-id': tenantId }, silent: true },
      );
      const ids = new Set<string>((data.terminals || []).map((t: any) => t.terminalId));
      setIsOnline(ids.has(terminalId));
    } catch { /* non-critical */ }
  }, [tenantId, terminalId]);

  useEffect(() => {
    if (!terminal) {
      fetchTerminal();
    }
    fetchWsStatus();
  }, [terminal, fetchTerminal, fetchWsStatus]);

  // The till's own signal — keep re-reading it so Online/Offline and the
  // status follow the device without a page reload.
  useEffect(() => {
    const id = window.setInterval(fetchWsStatus, 15000);
    return () => window.clearInterval(id);
  }, [fetchWsStatus]);

  if (loading) {
    // Skeleton-first: mirror the real layout (black header + tab rail +
    // light content cards) so nothing jumps when the terminal record lands.
    return (
      <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
        <div className="relative bg-black">
          <RubyLoader variant="overlay" size={36} />
          <div className="px-4 pt-4 pb-0">
            <Skeleton className="h-4 w-28 mb-4" />
            <div className="flex items-center gap-3 mb-1">
              <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <Skeleton className="h-3 w-56" />
              </div>
            </div>
          </div>
          <div className="px-4 pb-3 pt-3 flex gap-2">
            <Skeleton className="h-9 w-24 rounded-lg" />
            <Skeleton className="h-9 w-32 rounded-lg" />
            <Skeleton className="h-9 w-28 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        <div className="p-4 md:p-6 lg:p-8 w-full max-w-[90%] mx-auto space-y-4">
          <div className="bg-white rounded-2xl border border-stone-200/60 p-5">
            <Skeleton variant="light" className="h-4 w-36 mb-4" />
            <SkeletonRows variant="light" count={3} />
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/60 p-5">
            <Skeleton variant="light" className="h-4 w-28 mb-4" />
            <SkeletonRows variant="light" count={2} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !terminal) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(`/tenant/${tenantId}?tab=pos`)}
          className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-700 text-sm mb-4"
        >
          <ChevronLeft size={16} />
          Back to Tenant
        </button>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
          <Monitor size={32} className="mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500">{error || 'Terminal not found'}</p>
        </div>
      </div>
    );
  }

  // Online terminals are created already-active (no PROVISION# row, no device
  // to activate) — never treat them as pending/expired.
  const isOnlineSalesTerminal = terminal.terminalType === 'online_sales';
  // From the till's live connection + register row, not the provision row.
  const display = terminalDisplayStatus(terminal, isOnline);

  // Online terminals show Details + Online Payment; if the URL points at a
  // hidden tab (e.g. ?tab=emv-device), fall back to Details.
  const visibleTabs = isOnlineSalesTerminal
    ? ONLINE_TABS
    : catalog.policy
      ? TABS.map((t) => (t.id === 'emv-device' ? { ...t, label: CARD_SETUP_TAB_LABEL } : t))
      : TABS;
  const effectiveTab: TabId = visibleTabs.some(t => t.id === activeTab) ? activeTab : 'details';

  return (
    <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
      {/* Header */}
      <div className="bg-black">
        <div className="px-4 pt-4 pb-0">
          {/* Back button */}
          <button
            onClick={() => leaveGuard.requestLeave(() => navigate(`/tenant/${tenantId}?tab=pos`))}
            className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3 transition-colors"
          >
            <ChevronLeft size={16} />
            Back to Tenant
          </button>

          {/* Name + Status */}
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 rounded-xl bg-gray-800 border border-gray-700 shrink-0">
              <Monitor size={18} className="text-white/70" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-white text-lg font-bold truncate">{terminal.registerName}</h1>
                {statusBadge(display)}
                {display.registered && (
                  <span title={isOnlineSalesTerminal ? 'Active' : isOnline ? 'Online' : 'Offline'}>
                    <Activity
                      size={16}
                      className={isOnlineSalesTerminal || isOnline ? 'text-emerald-400' : 'text-red-400'}
                    />
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm mt-0.5 truncate">
                {terminal.locationName || terminal.locationId} · {typeLabel(terminal.terminalType)}
                {tenantName && <span className="text-gray-500"> · {tenantName}</span>}
              </p>
            </div>
          </div>

          {/* Lifecycle actions — online terminals only */}
          {isOnlineSalesTerminal && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {lifeStatus === 'archived' ? (
                <>
                  <span className="text-[11px] font-semibold text-amber-300 bg-amber-500/15 rounded-full px-2.5 py-1 inline-flex items-center gap-1"><Archive size={11} /> Archived</span>
                  <button disabled={lifeBusy} onClick={() => setStatus(terminal.terminalId, 'inactive')} className="h-8 px-3 rounded-lg bg-white/5 ring-1 ring-white/10 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"><RotateCcw size={13} /> Recover</button>
                  <button disabled={lifeBusy} onClick={() => setConfirmAction('delete')} className="h-8 px-3 rounded-lg ring-1 ring-red-500/30 text-red-300 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-red-500/10 transition-colors"><Trash2 size={13} /> Delete permanently</button>
                </>
              ) : (
                <>
                  {lifeStatus === 'inactive'
                    ? <button disabled={lifeBusy} onClick={() => setStatus(terminal.terminalId, 'active')} className="h-8 px-3 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-300 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-emerald-500/25 transition-colors"><Power size={13} /> Activate</button>
                    : <button disabled={lifeBusy} onClick={() => setStatus(terminal.terminalId, 'inactive')} className="h-8 px-3 rounded-lg bg-white/5 ring-1 ring-white/10 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"><Power size={13} /> Deactivate</button>}
                  <button disabled={lifeBusy} onClick={() => setConfirmAction('archive')} className="h-8 px-3 rounded-lg ring-1 ring-amber-500/30 text-amber-300 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-amber-500/10 transition-colors"><Archive size={13} /> Archive</button>
                </>
              )}
              {lifeBusy && <RubyGemMark size={14} drawing />}
              {lifeMsg && <span className="text-red-300 text-xs">{lifeMsg}</span>}
            </div>
          )}
        </div>

        {/* Tab bar */}
        <div className="px-4 pb-3 pt-3">
          <div className="flex gap-2 flex-wrap">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = effectiveTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors min-h-[36px] ${
                    isActive
                      ? 'bg-ruby text-white border border-ruby'
                      : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6 lg:p-8 w-full max-w-[90%] mx-auto">
        {effectiveTab === 'details' && !isOnlineSalesTerminal && (
          <ModelMismatchBanner
            tenantId={tenantId!}
            terminalId={terminal.terminalId}
            report={modelReport}
            onSwitched={onModelChanged}
          />
        )}
        {effectiveTab === 'details' && !isOnlineSalesTerminal && (
          <div className="mb-6">
            <TerminalActivityCard
              load={() => authedRequest<TerminalActivityData>(
                `${getPosRegisterUrl()}/pos/terminals/${encodeURIComponent(terminal.terminalId)}/activity?tenantId=${encodeURIComponent(tenantId!)}`,
                { headers: { 'x-tenant-id': tenantId! }, silent: true },
              )}
            />
          </div>
        )}
        {effectiveTab === 'details' && (
          <PosTerminalDetailsTab terminal={terminal} isOnline={isOnline} />
        )}
        {effectiveTab === 'online-terminals' && (
          <PosOnlineTerminalsTab terminal={terminal} tenantId={tenantId!} />
        )}
        {effectiveTab === 'payment-types' && (
          <PosPaymentTypesTab
            terminal={terminal as any}
            tenantId={tenantId!}
            tenantName={tenantName}
            isOnline={isOnline}
            onDirtyChange={setPaymentTypesDirty}
            onNavigateToEmvDevice={() => setActiveTab('emv-device')}
            {...(catalog.policy ? { pendingTipLocation: pendingTip, onPendingTipLocation: setPendingTip } : {})}
          />
        )}
        {effectiveTab === 'emv-device' && (
          <PosEmvDeviceTab
            terminal={terminal}
            tenantId={tenantId!}
            onNavigateToPaymentTypes={() => setActiveTab('payment-types')}
            {...(catalog.policy && catalog.model
              ? { cardSetup: { model: catalog.model, policy: catalog.policy, reported, reportedBuildTarget, onModelChanged, pendingTipLocation: pendingTip } }
              : {})}
          />
        )}
        {effectiveTab === 'control' && (
          <PosControlTab terminal={terminal} tenantId={tenantId!} onDeregistered={fetchTerminal} />
        )}
        {effectiveTab === 'button-layout' && (
          <PosButtonLayoutTab terminal={terminal} tenantId={tenantId!} />
        )}
      </div>

      {leaveGuard.dialog}

      {/* Archive / delete confirmation — custom (no native confirm) */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !lifeBusy && setConfirmAction(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${confirmAction === 'delete' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                  {confirmAction === 'delete' ? <Trash2 size={18} /> : <Archive size={18} />}
                </div>
                <h3 className="text-stone-900 font-bold text-base">{confirmAction === 'delete' ? 'Delete permanently?' : 'Archive this terminal?'}</h3>
              </div>
              <button onClick={() => setConfirmAction(null)} className="text-stone-400 hover:text-stone-600"><X size={18} /></button>
            </div>
            <p className="text-stone-600 text-sm mb-5 leading-relaxed">
              {confirmAction === 'delete'
                ? 'This permanently removes the archived terminal — it cannot be recovered afterward. The Dejavoo processor it referenced is left intact.'
                : 'The terminal is hidden from the list but fully kept — you can recover it anytime. It stops taking online payments until recovered. Its processor is untouched.'}
            </p>
            <div className="flex justify-end gap-2">
              <button disabled={lifeBusy} onClick={() => setConfirmAction(null)} className="h-10 px-4 rounded-lg ring-1 ring-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50">Cancel</button>
              <button disabled={lifeBusy}
                onClick={() => confirmAction === 'delete' ? deletePermanently(terminal.terminalId) : setStatus(terminal.terminalId, 'archived')}
                className={`h-10 px-4 rounded-lg text-white text-sm font-bold inline-flex items-center gap-2 ${confirmAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                {lifeBusy && <RubyGemMark size={15} drawing color="currentColor" />}
                {confirmAction === 'delete' ? 'Delete permanently' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPosTerminalDetail;
